import type { StatRow } from '@betreuung/shared';
import { personVars } from '@/lib/personColor';
import type { PersonLookup } from '@/lib/persons';
import { de } from '@/i18n/de';
import { describeStatRow, statRowKey } from './StatRowItem';

const RADIUS = 52;
const STROKE = 20;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export interface DonutProps {
  rows: readonly StatRow[];
  persons: PersonLookup;
  year: number;
  days: number;
}

/**
 * The year's shares as a ring. Each segment is one full circle whose dash
 * pattern exposes only its own arc; the rotation moves the start from 3 o'clock
 * to the top.
 */
export function Donut({ rows, persons, year, days }: DonutProps) {
  let start = 0;
  const segments = rows
    .filter((row) => row.slots > 0)
    .map((row) => {
      const length = row.fraction * CIRCUMFERENCE;
      const segment = { row, length, offset: start };
      start += length;
      return segment;
    });

  return (
    <div className="flex justify-center pt-1.5 pb-2.5">
      <svg
        width={170}
        height={170}
        viewBox="0 0 170 170"
        role="img"
        aria-label={`${year}, ${de.year.days(days)}`}
      >
        <circle cx={85} cy={85} r={RADIUS} fill="none" strokeWidth={STROKE} className="stroke-divider" />
        {segments.map(({ row, length, offset }) => {
          const { colorIndex } = describeStatRow(row, persons);
          return (
            <circle
              key={statRowKey(row)}
              cx={85}
              cy={85}
              r={RADIUS}
              fill="none"
              strokeWidth={STROKE}
              strokeDasharray={`${length} ${CIRCUMFERENCE - length}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 85 85)"
              style={colorIndex === null ? undefined : personVars(colorIndex)}
              className={colorIndex === null ? 'stroke-free-dash' : 'stroke-(--person)'}
            />
          );
        })}
        <text x={85} y={81} textAnchor="middle" className="fill-fg text-donut font-bold">
          {year}
        </text>
        <text
          x={85}
          y={99}
          textAnchor="middle"
          className="fill-fg-hint font-mono text-meta font-medium"
        >
          {de.year.days(days)}
        </text>
      </svg>
    </div>
  );
}
